import { useForm, UseFormReturn } from 'react-hook-form';
import {
  AiAssetStyles,
  AssetOrigin,
  baseSubtitles,
  defaultThumbnailConfig,
  defaultVideoOptions,
  Video,
  VideoType,
} from '@monorepo/types';
import { Button, Card, Typography } from 'antd';
import { LoadingState, useLoading } from '@monorepo/react-components';
import { useStore } from '../../helpers/use-store';
import { ThunderboltOutlined } from '@ant-design/icons';

import './video-form.scss';
import { useNavigate } from 'react-router-dom';
import BaseVideoForm, {
  BaseVideoFormType,
} from '../base-video-form/base-video-form';
import { useNotification } from '../../hooks/use-notification';
import SubscribeCTA from '../../components/subscribe-cta/subscribe-cta';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

const VideoForm = () => {
  const { t } = useTranslation();
  const {
    dataStore: { voiceoverStore, assetStore, userStore, videoStore },
  } = useStore();
  const { updateLoadingState, loadingState } = useLoading();
  const notificationApi = useNotification();
  const navigate = useNavigate();

  const form = useForm<Video>({
    mode: 'all',
    defaultValues: {
      title: Date.now().toString(),
      script: '',
      type: VideoType.Text,
      language: 'en',
      voiceoverId:
        voiceoverStore.items.find((voiceover) => voiceover.name === 'Brian')
          ?._id || '',
      backgroundMusicId: '',
      assetOrigin: AssetOrigin.AIImages,
      assetStyle: AiAssetStyles.CINEMATIC,
      subtitlesConfig: baseSubtitles,
      options: defaultVideoOptions,
      thumbnailConfig: {
        ...defaultThumbnailConfig,
        enabled: true,
        enableVoiceover: true,
      },
    },
  });

  const onSubmit = async (data) => {
    updateLoadingState(LoadingState.Loading);

    try {
      console.log('Form submitted:', data);

      // If an audio file was selected, upload it before saving the video
      if (data.audioFile instanceof File) {
        const asset = await assetStore.upload({
          file: data.audioFile,
          sceneId: data.sceneId,
          videoId: data.videoId,
        });

        data.backgroundMusicUrl = asset.url;
        data.backgroundMusicId = '';
        delete data.audioFile; // don’t send raw File
      }

      const videoId = (await videoStore.create(data)) as unknown as number;

      userStore.getLatestUser();

      notificationApi.success({
        placement: 'top',
        message: t('common.success'),
        description: t('videoForm.createSuccess'),
      });

      navigate(`/videos/${videoId}`);
    } catch (e) {
      notificationApi.error({
        placement: 'top',
        icon: '',
        message: t('common.error'),
        description: t('videoForm.createError'),
      });

      console.error(`failed creating video`, e);
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  const isLoading = loadingState === LoadingState.Loading;

  const isAllowedToCreate = userStore.canCreateVideos;

  return (
    <div className="video-form">
      <Helmet>
        <title>Create New Video | ShortsNinja</title>
        <meta
          name="description"
          content="Create a new faceless video with AI voiceover. Set up your video automation parameters and start creating content."
        />
        <meta
          name="keywords"
          content="create video, new video, ShortsNinja, faceless videos, ai video automation, ai voiceover"
        />
      </Helmet>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Card>
          <BaseVideoForm
            useCollapse={false}
            showScriptWriter={true}
            form={form as unknown as UseFormReturn<BaseVideoFormType>}
          />
        </Card>
        <div className={'create-button'}>
          <Button
            style={{ marginTop: '15px' }}
            disabled={!form.formState.isValid}
            loading={isLoading}
            icon={
              <ThunderboltOutlined
                style={{ fontSize: '18px', marginTop: '2px' }}
              />
            }
            type="primary"
            htmlType="submit"
            size="large"
            block
          >
            {t('videoForm.createButton')}
          </Button>
          {/*{!isAllowedToCreate ? <SubscribeCTA /> : null}*/}
          {isLoading ? (
            <Typography.Text
              style={{ display: 'flex', justifyContent: 'center' }}
              type={'secondary'}
            >
              {t('videoForm.creating')} ⚡️
            </Typography.Text>
          ) : null}
        </div>
      </form>
    </div>
  );
};

export default VideoForm;
